import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth-context';
import { 
  Activity, TrendingUp, TrendingDown, CheckCircle, 
  AlertCircle, Calendar, ExternalLink, ChevronLeft, Loader2
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import Navigation from './Navigation';

export default function AssessmentPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [currentAssessment, setCurrentAssessment] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedView, setSelectedView] = useState('current');

  useEffect(() => {
    if (id) {
      fetchAssessmentDetails(id);
    } else {
      fetchLatestAndHistory();
    }
  }, [id]);

  const fetchLatestAndHistory = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const apiUrl = `${import.meta.env.VITE_API_URL || 'http://localhost:3001'}`;
      
      const [latestRes, historyRes] = await Promise.all([
        fetch(`${apiUrl}/api/assessment/latest`, {
          credentials: 'include',
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${apiUrl}/api/assessment/history`, {
          credentials: 'include',
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);

      const latestData = await latestRes.json();
      const historyData = await historyRes.json();

      if (latestData.hasAssessment) {
        setCurrentAssessment(latestData.assessment);
      }
      setHistory(historyData.assessments || []);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching assessments:', error);
      setLoading(false);
    }
  };

  const fetchAssessmentDetails = async (assessmentId) => {
    try {
      const token = localStorage.getItem('authToken');
      const apiUrl = `${import.meta.env.VITE_API_URL || 'http://localhost:3001'}/api/assessment/${assessmentId}`;
      
      const response = await fetch(apiUrl, {
        credentials: 'include',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      
      if (data.success && data.assessment) {
        setCurrentAssessment(data.assessment);
      }
      setLoading(false);
    } catch (error) {
      console.error('Error fetching assessment details:', error);
      setLoading(false);
    }
  };

  const getScoreColor = (score) => {
    if (score >= 8.0) return 'text-green-600';
    if (score >= 6.0) return 'text-blue-600';
    if (score >= 4.0) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getProgressBarColor = (score) => {
    if (score >= 8.0) return 'bg-green-500';
    if (score >= 6.0) return 'bg-blue-500';
    if (score >= 4.0) return 'bg-yellow-500';
    return 'bg-red-500';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50">
        <Navigation />
        <div className="container mx-auto px-4 py-8">
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
          </div>
        </div>
      </div>
    );
  }

  if (!currentAssessment) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50">
        <Navigation />
        <div className="container mx-auto px-4 py-8">
          <Card className="max-w-2xl mx-auto">
            <CardContent className="pt-6 text-center">
              <Activity className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h2 className="text-2xl font-semibold mb-2">No Assessment Yet</h2>
              <p className="text-gray-600 mb-6">
                Take your first pitch readiness assessment to get started.
              </p>
              <a
                href={`https://pitchready.impactacceleratorcircle.com?email=${encodeURIComponent(user?.email || '')}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors"
              >
                Take Assessment
                <ExternalLink className="w-5 h-5" />
              </a>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50">
      <Navigation />
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              onClick={() => navigate('/dashboard')}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ChevronLeft className="w-5 h-5" />
            </Button>
            <h1 className="text-3xl font-bold">Pitch Readiness Assessment</h1>
          </div>

          <div className="flex gap-2">
            <Button
              onClick={() => setSelectedView('current')}
              variant={selectedView === 'current' ? 'default' : 'outline'}
            >
              Current
            </Button>
            <Button
              onClick={() => setSelectedView('history')}
              variant={selectedView === 'history' ? 'default' : 'outline'}
            >
              History
            </Button>
          </div>
        </div>

        {selectedView === 'current' ? (
          <>
            {/* Overall Score Card */}
            <Card className="mb-6">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Overall Score</CardTitle>
                    <CardDescription>
                      Assessment #{currentAssessment.assessment_number} • 
                      {' '}{new Date(currentAssessment.completed_at).toLocaleDateString('en-US', {
                        month: 'long',
                        day: 'numeric',
                        year: 'numeric'
                      })}
                    </CardDescription>
                  </div>
                  <a
                    href={`https://pitchready.impactacceleratorcircle.com?email=${encodeURIComponent(user?.email || '')}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 text-blue-600 hover:text-blue-700"
                  >
                    Retake Assessment
                    <ExternalLink className="w-4 h-4" />
                  </a>
                </div>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-12">
                  <div className="text-center">
                    <div className={`text-6xl font-bold ${getScoreColor(currentAssessment.overall_score)}`}>
                      {parseFloat(currentAssessment.overall_score).toFixed(1)}
                    </div>
                    <div className="text-gray-500 mt-2">out of 10</div>
                  </div>

                  <div className="flex-1">
                    <div className={`text-2xl font-semibold ${getScoreColor(currentAssessment.overall_score)} mb-2`}>
                      {currentAssessment.readiness_verdict}
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-4">
                      <div
                        className={`h-4 rounded-full ${getProgressBarColor(currentAssessment.overall_score)}`}
                        style={{ width: `${(parseFloat(currentAssessment.overall_score) / 10) * 100}%` }}
                      ></div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Top Performers & Areas for Improvement */}
            <div className="grid md:grid-cols-2 gap-6 mb-6">
              {/* Top Performers */}
              <Card className="bg-green-50 border-2 border-green-200">
                <CardHeader>
                  <div className="flex items-center gap-3">
                    <CheckCircle className="w-6 h-6 text-green-600" />
                    <CardTitle className="text-green-900">Top Performing Areas</CardTitle>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {currentAssessment.topPerformers?.map((section, idx) => (
                      <div key={idx}>
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-sm font-medium text-green-900">
                            {section.section_name}
                          </span>
                          <span className="text-sm font-bold text-green-600">
                            {parseFloat(section.section_score).toFixed(1)}
                          </span>
                        </div>
                        <div className="w-full bg-green-100 rounded-full h-2">
                          <div
                            className="bg-green-500 h-2 rounded-full"
                            style={{ width: `${(parseFloat(section.section_score) / 10) * 100}%` }}
                          ></div>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Areas for Improvement */}
              <Card className="bg-red-50 border-2 border-red-200">
                <CardHeader>
                  <div className="flex items-center gap-3">
                    <AlertCircle className="w-6 h-6 text-red-600" />
                    <CardTitle className="text-red-900">Areas for Improvement</CardTitle>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {currentAssessment.needsImprovement?.map((section, idx) => (
                      <div key={idx}>
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-sm font-medium text-red-900">
                            {section.section_name}
                          </span>
                          <span className="text-sm font-bold text-red-600">
                            {parseFloat(section.section_score).toFixed(1)}
                          </span>
                        </div>
                        <div className="w-full bg-red-100 rounded-full h-2">
                          <div
                            className="bg-red-500 h-2 rounded-full"
                            style={{ width: `${(parseFloat(section.section_score) / 10) * 100}%` }}
                          ></div>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* All Sections Breakdown */}
            <Card>
              <CardHeader>
                <CardTitle>Complete Section Breakdown</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {currentAssessment.section_scores?.map((item, idx) => (
                    <div key={idx}>
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-medium">{item.section}</span>
                        <span className={`font-bold ${getScoreColor(item.score)}`}>
                          {parseFloat(item.score).toFixed(1)} / 10
                        </span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-3">
                        <div
                          className={`h-3 rounded-full ${getProgressBarColor(item.score)}`}
                          style={{ width: `${(parseFloat(item.score) / 10) * 100}%` }}
                        ></div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </>
        ) : (
          /* History View */
          <Card>
            <CardHeader>
              <CardTitle>Assessment History</CardTitle>
            </CardHeader>
            <CardContent>
              {history.length === 0 ? (
                <p className="text-gray-600">No previous assessments yet.</p>
              ) : (
                <div className="space-y-4">
                  {history.map((assessment, idx) => {
                    const improvement = idx < history.length - 1 
                      ? parseFloat(assessment.overall_score) - parseFloat(history[idx + 1].overall_score) 
                      : null;
                    
                    return (
                      <div
                        key={assessment.id}
                        onClick={() => {
                          setSelectedView('current');
                          fetchAssessmentDetails(assessment.id);
                        }}
                        className="border rounded-lg p-4 hover:bg-gray-50 cursor-pointer transition-colors"
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-4">
                            <div>
                              <div className="flex items-center gap-2">
                                <Calendar className="w-4 h-4 text-gray-400" />
                                <span className="text-sm text-gray-600">
                                  {new Date(assessment.completed_at).toLocaleDateString('en-US', {
                                    month: 'short',
                                    day: 'numeric',
                                    year: 'numeric'
                                  })}
                                </span>
                              </div>
                              <div className="text-sm text-gray-500 mt-1">
                                Assessment #{assessment.assessment_number}
                              </div>
                            </div>
                          </div>

                          <div className="flex items-center gap-4">
                            {improvement !== null && (
                              <div className={`flex items-center gap-1 ${
                                improvement > 0 ? 'text-green-600' : 'text-red-600'
                              }`}>
                                {improvement > 0 ? (
                                  <TrendingUp className="w-4 h-4" />
                                ) : (
                                  <TrendingDown className="w-4 h-4" />
                                )}
                                <span className="text-sm font-medium">
                                  {improvement > 0 ? '+' : ''}{improvement.toFixed(1)}
                                </span>
                              </div>
                            )}

                            <div className={`text-2xl font-bold ${getScoreColor(assessment.overall_score)}`}>
                              {parseFloat(assessment.overall_score).toFixed(1)}
                            </div>

                            <div className="text-right min-w-[120px]">
                              <div className={`text-sm font-semibold ${getScoreColor(assessment.overall_score)}`}>
                                {assessment.readiness_verdict}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}

